import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GraduationCapIcon, ShieldCheckIcon, CheckCircleIcon, ChevronDownIcon, InfoIcon, TrendingUpIcon } from 'lucide-react';
import { formatNaira } from '../utils/format';
import { TopBar } from '../components/TopBar';
interface ProductDetailProps {
  navigate: (screen: string) => void;
}
export function ProductDetail({
  navigate
}: ProductDetailProps) {
  const [targetAmount, setTargetAmount] = useState<number>(10000000);
  const [durationYears, setDurationYears] = useState<number>(18);
  const [monthlyPremium, setMonthlyPremium] = useState<number>(0);
  const [totalContributions, setTotalContributions] = useState<number>(0);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  // Calculate PMT based on FV (using 10% for the base calculation)
  useEffect(() => {
    const r = 0.1 / 12;
    const n = durationYears * 12;
    if (n > 0) {
      const pmt = targetAmount * (r / (Math.pow(1 + r, n) - 1));
      setMonthlyPremium(pmt);
      setTotalContributions(pmt * n);
    }
  }, [targetAmount, durationYears]);
  // Calculate scenarios based on the computed PMT
  const n = durationYears * 12;
  const calcFV = (rate: number) => {
    const r = rate / 12;
    return monthlyPremium * ((Math.pow(1 + r, n) - 1) / r);
  };
  const scenarios = [{
    label: 'Conservative (8%)',
    value: calcFV(0.08),
    color: 'bg-gray-400'
  }, {
    label: 'Moderate (10%)',
    value: calcFV(0.1),
    color: 'bg-emerald-500'
  }, {
    label: 'Optimistic (12%)',
    value: calcFV(0.12),
    color: 'bg-amber-500'
  }];
  const maxScenarioValue = scenarios[2].value;
  const faqs = [{
    q: 'What happens if I die before maturity?',
    a: 'The full target amount (₦' + formatNaira(targetAmount).replace('₦', '') + ") is immediately guaranteed and paid out to your named beneficiary, ensuring your child's education is secured regardless."
  }, {
    q: 'What happens if I miss a payment?',
    a: 'You have a 30-day grace period. After that, your cover may be suspended until arrears are cleared, but your accumulated savings remain yours.'
  }, {
    q: 'Can I change my target amount later?',
    a: 'Yes, you can increase your target amount at any policy anniversary, which will adjust your monthly premium accordingly.'
  }, {
    q: 'Is my money safe?',
    a: 'Absolutely. All funds are managed by licensed underwriters regulated by NAICOM, and your life cover is legally binding.'
  }];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="flex-1 overflow-y-auto pb-24 bg-slate-50" data-id="element-750">
      {/* Hero Banner */}
      <div className="bg-gradient-to-b from-emerald-600 to-emerald-800 pt-safe pb-8 px-5 relative overflow-hidden rounded-b-[2.5rem] shadow-lg" data-id="element-751">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-20 -mt-20" data-id="element-752"></div>

        <div className="relative z-20" data-id="element-753">
          <TopBar showBack onBack={() => navigate('products')} transparent data-id="element-754" />
        </div>

        <div className="relative z-10 mt-2 text-center" data-id="element-755">
          <div className="w-20 h-20 bg-white/20 backdrop-blur-md rounded-3xl flex items-center justify-center mx-auto mb-4 border border-white/30 shadow-xl" data-id="element-756">
            <GraduationCapIcon className="w-10 h-10 text-white" data-id="element-757" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2 tracking-tight" data-id="element-758">
            Child Education
          </h1>
          <div className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 px-3 py-1 rounded-full text-emerald-50 text-xs font-bold tracking-wide backdrop-blur-sm" data-id="element-759">
            <ShieldCheckIcon className="w-3.5 h-3.5" data-id="element-760" /> Insurance-Backed Savings
          </div>
        </div>
      </div>

      <div className="px-5 pt-4 pb-24" data-id="element-761">
        {/* Header Section */}
        <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 mb-6 text-center" data-id="element-762">
          <div className="w-16 h-16 rounded-2xl bg-amber-50 flex items-center justify-center text-amber-500 mx-auto mb-4" data-id="element-763">
            <GraduationCapIcon className="w-8 h-8" data-id="element-764" />
          </div>
          <h1 className="text-2xl font-bold text-[#1B3B36] mb-2" data-id="element-765">
            Child Education Plan
          </h1>
          <p className="text-sm text-gray-500 mb-4" data-id="element-766">By Leadway Assurance Plc</p>
          <div className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-full text-xs font-bold" data-id="element-767">
            <ShieldCheckIcon className="w-4 h-4" data-id="element-768" /> Capital Protected
          </div>
        </div>

        {/* Value Proposition */}
        <div className="mb-8" data-id="element-769">
          <h2 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4 px-2" data-id="element-770">
            Why choose this plan?
          </h2>
          <div className="space-y-3" data-id="element-771">
            {['Guaranteed payout for university fees', 'Life cover included for the parent/guardian', 'Flexible premium payment options', 'Tax-free returns on maturity'].map((benefit, idx) => <div key={idx} className="flex items-start gap-3 bg-white p-4 rounded-2xl border border-gray-50 shadow-sm" data-id="element-772">
                <CheckCircleIcon className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" data-id="element-773" />
                <span className="text-sm font-medium text-[#1B3B36] leading-relaxed" data-id="element-774">
                  {benefit}
                </span>
              </div>)}
          </div>
        </div>

        {/* Interactive Calculator */}
        <div className="bg-[#1B3B36] rounded-3xl p-6 text-white mb-8 shadow-xl shadow-emerald-900/20 relative overflow-hidden" data-id="element-775">
          {/* Decorative background elements */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full blur-2xl -mr-10 -mt-10" data-id="element-776"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-amber-500/10 rounded-full blur-xl -ml-10 -mb-10" data-id="element-777"></div>

          <div className="relative z-10" data-id="element-778">
            <div className="flex items-center gap-2 mb-6" data-id="element-779">
              <TrendingUpIcon className="w-5 h-5 text-amber-400" data-id="element-780" />
              <h2 className="text-lg font-bold" data-id="element-781">Premium Calculator</h2>
            </div>

            <div className="space-y-6 mb-8" data-id="element-782">
              <div data-id="element-783">
                <div className="flex justify-between items-end mb-2" data-id="element-784">
                  <label className="text-xs font-bold text-gray-300 uppercase tracking-wider" data-id="element-785">
                    Target Amount
                  </label>
                  <span className="text-xl font-bold text-amber-400" data-id="element-786">
                    {formatNaira(targetAmount)}
                  </span>
                </div>
                <input type="range" min="1000000" max="50000000" step="500000" value={targetAmount} onChange={e => setTargetAmount(Number(e.target.value))} className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer accent-amber-400" data-id="element-787" />
              </div>

              <div data-id="element-788">
                <div className="flex justify-between items-end mb-2" data-id="element-789">
                  <label className="text-xs font-bold text-gray-300 uppercase tracking-wider" data-id="element-790">
                    Duration
                  </label>
                  <span className="text-xl font-bold text-amber-400" data-id="element-791">
                    {durationYears} Years
                  </span>
                </div>
                <input type="range" min="5" max="25" step="1" value={durationYears} onChange={e => setDurationYears(Number(e.target.value))} className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer accent-amber-400" data-id="element-792" />
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/10" data-id="element-793">
              <div className="flex justify-between items-center mb-3 pb-3 border-b border-white/10" data-id="element-794">
                <span className="text-sm text-gray-300" data-id="element-795">Estimated Monthly</span>
                <span className="text-2xl font-bold text-white" data-id="element-796">
                  {formatNaira(monthlyPremium)}
                </span>
              </div>
              <div className="flex justify-between items-center" data-id="element-797">
                <span className="text-xs text-gray-400 flex items-center gap-1" data-id="element-798">
                  Total Contributions <InfoIcon className="w-3 h-3" data-id="element-799" />
                </span>
                <span className="text-sm font-bold text-gray-300" data-id="element-800">
                  {formatNaira(totalContributions)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* FAQs */}
        <div className="mb-8" data-id="element-801">
          <h2 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4 px-2" data-id="element-802">
            Common Questions
          </h2>
          <div className="space-y-3" data-id="element-803">
            {[{
            q: 'What happens if I miss a payment?',
            a: 'You have a 30-day grace period to make up for missed payments without any penalties.'
          }, {
            q: 'Can I withdraw before maturity?',
            a: 'Yes, partial withdrawals are allowed after the 3rd year, subject to terms and conditions.'
          }, {
            q: 'How does the life cover work?',
            a: 'In the event of the parent/guardian passing, the target amount is immediately paid out to ensure the childs education continues.'
          }].map((faq, idx) => <div key={idx} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-id="element-804">
                <button onClick={() => setOpenFaqIndex(openFaqIndex === idx ? null : idx)} className="w-full px-5 py-4 flex items-center justify-between text-left" data-id="element-805">
                  <span className="font-bold text-[#1B3B36] text-sm pr-4" data-id="element-806">
                    {faq.q}
                  </span>
                  <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform ${openFaqIndex === idx ? 'rotate-180' : ''}`} data-id="element-807" />
                </button>
                <AnimatePresence data-id="element-808">
                  {openFaqIndex === idx && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="px-5 pb-4 text-sm text-gray-500 leading-relaxed" data-id="element-809">
                      {faq.a}
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
      </div>

      {/* Fixed Bottom Action */}
      <div className="fixed sm:absolute bottom-0 w-full max-w-[430px] bg-white/80 backdrop-blur-xl border-t border-gray-100 p-4 pb-safe z-50" data-id="element-810">
        <button onClick={() => navigate('plan-selection')} className="w-full bg-amber-500 hover:bg-amber-400 text-[#1B3B36] font-bold py-4 rounded-xl shadow-lg shadow-amber-500/30 active:scale-95 transition-transform text-lg" data-id="element-811">
          View Plans
        </button>
      </div>
    </motion.div>;
}